import { filter } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuComponent } from '../../components/menu/menu.component';
import {
	ApiService,
	CommonFunctionsService,
	DatastoreService,
} from '../../services';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '../../../environments/environment';
import moment from 'moment-timezone';
import { GovukInitService } from '../../services/govuk-init.service';
import { isExtendAvailable } from '../extend/extend-allowed.guard';
import { isOurCeremonyFeatureEnabled } from '../../services/guards/feature-toggle.guard';
import { isRescheduleAvailable } from '../reschedule/reschedule-allowed.guard';
import { isExternalVenueBooking } from '../../services/guards/external-venue-add-ons.guard';
import { linksIncludeExtensionItem } from '../extend/extend-not-extended.guard';
import { resolveVenueName } from '../../services/venue-name.util';

@Component({
	selector: 'app-our-ceremony',
	templateUrl: './our-ceremony.component.html',
	styleUrls: ['./our-ceremony.component.scss'],
})
export class OurCeremonyComponent implements OnInit {
	config: any;
	site: string = '';
	companyDetails: any;
	bookings = [];
	ceremony: any = false;

	party1Name: string = '';
	party2Name: string = '';
	venueName: string = '';
	leadBooker: any = false;
	modifyBtnShown: boolean = false;
	canReschedule: boolean = false;

	// Hides the Extend button until checkForExtensionItem has resolved, so it
	// doesn't flash visible while the links are still loading.
	extensionItemCheckPending = true;

	// Hides the Add ons button until the booking + service have loaded, so it
	// doesn't flash visible for an external-venue booking.
	addOnsCheckPending = true;

	// Extend is gated on the booking's resource (allowlist from config at
	// myCeremony.extendCeremony.showExtendButtonForResourceIds), not its service
	// id. The allowlist + resolver are shared with extendAllowedGuard so the
	// button and the route guard stay in lock-step.
	get canExtend(): boolean {
		if (this.extensionItemCheckPending) {
			return false;
		}
		return isExtendAvailable(this.dataStore);
	}

	get canPurchaseAddons(): boolean {
		if (this.addOnsCheckPending) {
			return false;
		}
		return (
			isOurCeremonyFeatureEnabled(this.dataStore, 'can_purchase_addons') &&
			!isExternalVenueBooking(this.dataStore)
		);
	}

	get canCancel(): boolean {
		return isOurCeremonyFeatureEnabled(this.dataStore, 'can_cancel');
	}

	/**
	 * A ceremony may only be extended once. The extend flow stamps
	 * settings.extended_at on success, so its presence disables the button
	 * (RWRSPI-970). Bookings extended without the stamp are caught by
	 * checkForExtensionItem, which flags an "Extended ceremony time" extra-item
	 * link on the booking (RWRSPI-1010).
	 */
	get alreadyExtended(): boolean {
		return (
			!!this.ceremony?.settings?.extended_at ||
			this.dataStore.bookingHasExtensionItem
		);
	}

	constructor(
		private route: ActivatedRoute,
		private dataStore: DatastoreService,
		private commonFunctionsService: CommonFunctionsService,
		private apiService: ApiService,
		private spinner: NgxSpinnerService,
		private router: Router,
		private govuk: GovukInitService
	) { }

	ngOnInit(): void {
		this.route.params.subscribe(async (params) => {
			await this.commonFunctionsService.setCompanyName(params);
			this.site = params.site;
		});
		this.govuk.init();
		this.getBookings();
	}

	async getBookings() {
		try {
			const config = JSON.parse(this.dataStore.config);
			if (config.hasOwnProperty('myCeremony')) {
				let bookings = (await this.apiService.getBookings(
					environment.companyId,
					this.dataStore.member.id
				)) as any;
				this.dataStore.purchasedBookings = bookings._embedded.bookings.filter(
					(booking) => booking.company_id === 37004
				);
        console.log(config.myCeremony);
        if (config.myCeremony.hasOwnProperty('general')) {
          if (config.myCeremony.general.hasOwnProperty('excludedBookingsServiceIds')) {
            for (const serviceId of config.myCeremony.general.excludedBookingsServiceIds) {
              this.dataStore.purchasedBookings = this.dataStore.purchasedBookings.filter(
                (booking) => booking.service_id !== serviceId
              );
            }
            console.log('filtered bookings', this.dataStore.purchasedBookings);
          }
        }
				console.log('bookings: ', bookings);
				console.log('purchasedBookings: ', this.dataStore.purchasedBookings);
				this.bookings = this.dataStore.purchasedBookings;
				this.ceremony = this.bookings[0];
				this.dataStore.booking = this.ceremony;
				this.ceremony.just_date = moment(this.ceremony.datetime)
					.utc()
					.tz('Europe/London')
					.format('dddd Do MMMM YYYY');
				this.ceremony.just_time = moment(this.ceremony.datetime)
					.utc()
					.tz('Europe/London')
					.format('h:mmA');
				console.log(this.ceremony.datetime);
				this.ceremony.shortdate = moment(this.ceremony.datetime)
					.utc()
					.tz('Europe/London')
					.format('DD/MM/YYYY');
				this.canReschedule = isRescheduleAvailable(this.dataStore);
				this.venueName = this.ceremony.resource_name;
				this.leadBooker = this.dataStore.member;
				localStorage.setItem('ceremony', JSON.stringify(this.ceremony));
				const company = await this.apiService.getCompany(
					this.ceremony.company_id
				);
				this.dataStore.selectedCompany = company;
				localStorage.setItem('company', JSON.stringify(company));

				// booking questions
				if (config.myCeremony.hasOwnProperty('our-ceremony')) {
					if (config.myCeremony['our-ceremony'].hasOwnProperty('questions')) {
						// for (const key of Object.keys(config.myCeremony['our-ceremony'].questions)) {
						//   console.log(key, config.myCeremony['our-ceremony'].questions[key]);

						if (config.myCeremony['our-ceremony'].hasOwnProperty('bookingMoveOption')) {
							this.modifyBtnShown = true;
						}

						// }
						const party1FirstName = this.getBookingQuestionAnswer(
							config.myCeremony['our-ceremony'].questions.party1FirstName
						);
						const party1LastName = this.getBookingQuestionAnswer(
							config.myCeremony['our-ceremony'].questions.party1LastName
						);
						if (party1FirstName && party1LastName) {
							this.party1Name = `${party1FirstName.answer} ${party1LastName.answer}`;
						}

						const party2FirstName = this.getBookingQuestionAnswer(
							config.myCeremony['our-ceremony'].questions.party2FirstName
						);
						const party2LastName = this.getBookingQuestionAnswer(
							config.myCeremony['our-ceremony'].questions.party2LastName
						);
						if (party2FirstName && party2LastName) {
							this.party2Name = `${party2FirstName.answer} ${party2LastName.answer}`;
						}

						const service = (await this.apiService.getService(
							this.ceremony.company_id,
							this.ceremony.service_id
						)) as any;
						this.dataStore.selectedService = service;
						localStorage.setItem('service', JSON.stringify(service));
						this.venueName = resolveVenueName({
							resourceName: this.ceremony.resource_name,
							serviceGlobalId: service.global_id,
							bookingQuestions: this.ceremony.questions,
							questionsConfig:
								config.myCeremony['our-ceremony'].questions,
						});
					}
				}
				this.addOnsCheckPending = false;
				await this.checkForExtensionItem();
				await this.commonFunctionsService.setupCustomObjects(this.ceremony);

				const threads: any = await this.apiService.getThreadsForBooking(
					this.ceremony.company_id,
					this.ceremony.id
				);
				if (threads && threads.length > 0) {
					let activeThread = threads[0];
					sessionStorage.setItem('active_thread', JSON.stringify(activeThread));
					const messagesOnThread = await this.apiService.getMessagesInThread(
						this.ceremony.company_id,
						activeThread.thread_id,
						false
					);
					if (messagesOnThread) {
						sessionStorage.setItem(
							'active_thread_messages',
							JSON.stringify(messagesOnThread)
						);
					}
				}
			}
		} catch (err) {
			console.error(err);
		} finally {
			this.addOnsCheckPending = false;
		}
	}

	/**
	 * Flags bookings that carry an "Extended ceremony time" extra-item link,
	 * resolved by item_id against the service extras (the my-add-ons pattern).
	 * Feeds alreadyExtended and extendNotExtendedGuard via the datastore.
	 */
	async checkForExtensionItem() {
		try {
			const links = (await this.apiService.getServiceItemExtraLinksForBooking(
				this.ceremony.company_id,
				this.ceremony.id
			)) as any;
			if (!Array.isArray(links) || links.length === 0) {
				this.dataStore.bookingHasExtensionItem = false;
				return;
			}
			const globalServiceId =
				this.dataStore.selectedService?.global_id ?? this.ceremony.service_id;
			const serviceExtraItems = await this.apiService
				.getServiceItemExtras(
					this.ceremony.company_id,
					globalServiceId,
					undefined,
					true
				)
				.toPromise();
			this.dataStore.bookingHasExtensionItem = linksIncludeExtensionItem(
				links,
				serviceExtraItems ?? []
			);
		} catch (err) {
			console.error('Failed to check booking links for an extension item', err);
		} finally {
			this.extensionItemCheckPending = false;
		}
	}

	getBookingQuestionAnswer(questionId: number) {
		if (this.ceremony.questions.hasOwnProperty(questionId)) {
			const foundAnswer = this.ceremony.questions[questionId];
			return foundAnswer;
		}
		return false;
	}

	navigate(route: string) {
		this.router.navigate([
			`${this.commonFunctionsService.getCompanyName()}/${this.commonFunctionsService.getCompanyId()}${route}`,
		]);
	}
}
